import {useCallback, useEffect, useState} from 'react';

import {FormField} from 'shells/form/form-field';
import {FormWrapperWithButtons} from 'shells/form/form-wrapper-with-buttons';
import {ModalLightbox} from 'shells/modal/modal-lightbox';
import {TextField} from 'shells/textfield/textfield';

import {appHistory} from '../../../../history';

import {useAddSharedEmailAddress} from '../graphql/hooks/use-add-shared-email-address';

import styles from './shared-email-address-modal.module.css';

interface Props {
    readonly isOpen: boolean;
    readonly onClose: () => void;
}

export function AddSharedEmailAddressModal(props: Props) {
    const [emailAddress, setEmailAddress] = useState<string>('');
    const [displayName, setDisplayName] = useState<string>('');

    const {addSharedEmailAddress, isLoading, error} = useAddSharedEmailAddress();

    const resetFields = useCallback(() => {
        setEmailAddress('');
        setDisplayName('');
    }, []);

    useEffect(() => {
        if (!props.isOpen) {
            resetFields();
            return;
        }
        setDisplayName(NutClientConfig.instanceName || '');
    }, [props.isOpen, resetFields]);

    const handleClose = () => {
        resetFields();
        props.onClose();
    };

    const handleSubmit = async () => {
        const trimmedEmail = emailAddress.trim();
        if (!trimmedEmail) {
            return;
        }

        const trimmedDisplay = displayName.trim();
        if (!trimmedDisplay) {
            return;
        }

        const {data} = await addSharedEmailAddress({
            emailAddress: trimmedEmail,
            displayName: trimmedDisplay,
        });

        const fromAddressId = data?.sharedEmailAddressAdd.fromAddress.id;
        if (fromAddressId) {
            handleClose();
            appHistory.push(`/inbox/settings/email/${fromAddressId}`);
        }
    };

    if (!props.isOpen) {
        return null;
    }

    return (
        <ModalLightbox onClose={handleClose}>
            <div className={styles.container}>
                <FormWrapperWithButtons
                    title='Add a shared email address'
                    isSaving={isLoading}
                    onCancel={handleClose}
                    onSaveClick={handleSubmit}
                    buttonText='Add address'
                    savingText='Adding…'
                    saveButtonDisabled={
                        !emailAddress.trim().length || !displayName.trim().length || isLoading
                    }
                    resetOnSaveComplete={true}
                    errorMessage={error ?? undefined}
                    errorSaving={Boolean(error)}
                >
                    <div className='full-width'>
                        <FormField
                            innerId='shared-inbox-sender-name'
                            label='Sender name'
                            isRequired={true}
                        >
                            <TextField
                                value={displayName}
                                onChange={setDisplayName}
                                onEnter={handleSubmit}
                            />
                        </FormField>
                        <FormField
                            innerId='shared-inbox-email'
                            label='Email address'
                            isRequired={true}
                        >
                            <TextField
                                value={emailAddress}
                                onChange={setEmailAddress}
                                placeholder='sales@company.com'
                                focusOnMount={true}
                                onEnter={handleSubmit}
                            />
                        </FormField>
                    </div>
                </FormWrapperWithButtons>
            </div>
        </ModalLightbox>
    );
}
